// TODO: Ajouter des améliorations et fonctionnalités supplémentaires

import { RELATIVE_ROUTES, ROUTES, ROUTE_PATTERNS } from '@/constants/routes';
import { AuthProvider } from '@/contexts/AuthContext';
import { DashboardPreferencesProvider } from '@/contexts/DashboardPreferencesContext';
import { DevRoleDashboardProvider } from '@/contexts/DevRoleDashboardContext';
import { ReminderProvider } from '@/contexts/ReminderContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { StoreProvider } from '@/contexts/StoreContext';
import { ButtonColorsProvider } from '@/contexts/ButtonColorsContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import MainLayout from '@/layouts/MainLayout';
import { LoginPage } from '@/pages/LoginPage';
import ProtectedRoute from '@components/ProtectedRoute';
import RoutePermissionGuard from '@components/routing/RoutePermissionGuard';
import { AppErrorBoundary } from '@/components/errors/AppErrorBoundary';
import { SettingsFeatureFlagsProvider } from '@/contexts/SettingsFeatureFlagsContext';
import { ErrorBoundary, LoadingSpinner } from '@components/ui';
import ReminderSystem from '@/components/appointments/ReminderSystem';
import PaymentDeletionWatcher from '@/components/business/PaymentDeletionWatcher';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

// Lazy-loaded pages - chargement différé pour réduire le bundle initial
const Home = lazy(() => import('@/pages/Home'));
const HomeLuxori = lazy(() => import('@/pages/HomeLuxori'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const AchatsPage = lazy(() => import('@/pages/business/AchatsPage'));
const AfterSalesPage = lazy(() => import('@/pages/business/AfterSalesPage'));
const ArticlesPage = lazy(() => import('@/pages/business/ArticlesPage'));
const BonsDeCommandePage = lazy(() => import('@/pages/business/BonsDeCommandePage'));
const BonsDeCommandePage2 = lazy(() => import('@/pages/business/BonsDeCommandePage2'));
const ClientsPage = lazy(() => import('@/pages/business/ClientsPage'));
const InventairePage = lazy(() => import('@/pages/business/InventairePage'));
const Inventaire2023Page = lazy(() => import('@/pages/business/Inventaire2023Page'));
const FournisseursPage = lazy(() => import('@/pages/business/FournisseursPage'));
const SupplierOrdersPage = lazy(() => import('@/pages/business/SupplierOrdersPage'));
const PendingSupplierOrdersPage = lazy(() => import('@/pages/business/PendingSupplierOrdersPage'));
const PaiementsPage = lazy(() => import('@/pages/business/PaiementsPage'));
const FacturesAPayerPage = lazy(() => import('@/pages/business/FacturesAPayerPage'));
const CreditNotesFormPage = lazy(() => import('@/pages/business/CreditNotesFormPage'));
const CreditNotesPage = lazy(() => import('@/pages/business/CreditNotesPage'));
const DeliveriesPage = lazy(() => import('@/pages/business/DeliveriesPage'));
const DeliveryPreparationPage = lazy(() => import('@/pages/business/DeliveryPreparationPage'));
const DeliveryTimingsPage = lazy(() => import('@/pages/business/DeliveryTimingsPage'));
const FacturesAcomptePage = lazy(() => import('@/pages/business/FacturesAcomptePage'));
const FacturesPage = lazy(() => import('@/pages/business/FacturesPage'));
const InvoiceFormPage = lazy(() => import('@/pages/business/InvoiceFormPage'));
const LiftTypesPage = lazy(() => import('@/pages/business/LiftTypesPage'));
const OrderFormPage = lazy(() => import('@/pages/business/OrderFormPage'));
const OrderFormPage2 = lazy(() => import('@/pages/business/OrderFormPage2'));
const PaymentMethodsPage = lazy(() => import('@/pages/business/PaymentMethodsPage'));
const ServiceTypesPage = lazy(() => import('@/pages/business/ServiceTypesPage'));
const VendorsPage = lazy(() => import('@/pages/business/VendorsPage'));
const DailySalesPage = lazy(() => import('@/pages/DailySalesPage'));
const PeriodStatisticsPage = lazy(() => import('@/pages/PeriodStatisticsPage'));
const VendorStatisticsPage = lazy(() => import('@/pages/VendorStatisticsPage'));
const PaymentDeletionAuditPage = lazy(() => import('@/pages/business/PaymentDeletionAuditPage'));
const DevisPage = lazy(() => import('@/pages/business/DevisPage'));
const DevisFormPage = lazy(() => import('@/pages/business/DevisFormPage'));
const DeviceAuthorizationPage = lazy(() => import('@/pages/DeviceAuthorizationPage'));
const MarketingStudioListPage = lazy(() => import('@/pages/MarketingStudioListPage'));
const MarketingStudioPage = lazy(() => import('@/pages/MarketingStudioPage'));

// Settings sub-routes (loaded with SettingsPage)
const SettingsOverview = lazy(() => import('@/pages/settings/SettingsOverview'));
const SettingsAccessUsersRoute = lazy(
  () => import('@/pages/settings/access/SettingsAccessUsersRoute')
);
const SettingsAccessPermissionsRoute = lazy(
  () => import('@/pages/settings/access/SettingsAccessPermissionsRoute')
);
const SettingsAccessDevicesRoute = lazy(
  () => import('@/pages/settings/access/SettingsAccessDevicesRoute')
);

const ButtonColorSettings = lazy(() => import('@/components/settings/ButtonColorSettings'));
const DashboardButtonsByRoleSettings = lazy(
  () => import('@/components/settings/DashboardButtonsByRoleSettings')
);
const DeliveryTimingsSettings = lazy(() => import('@/components/settings/DeliveryTimingsSettings'));
const HomepageLinksSettings = lazy(() => import('@/components/settings/HomepageLinksSettings'));
const LiftTypesSettings = lazy(() => import('@/components/settings/LiftTypesSettings'));
const PaymentMethodsSettings = lazy(() => import('@/components/settings/PaymentMethodsSettings'));
const ServiceTypesSettings = lazy(() => import('@/components/settings/ServiceTypesSettings'));
const StoresManagement = lazy(() => import('@/components/settings/StoresManagement'));
const TermsAndConditionsSettings = lazy(
  () => import('@/components/settings/TermsAndConditionsSettings')
);
const VendorsSettings = lazy(() => import('@/components/settings/VendorsSettings'));
const GmailTestSettings = lazy(() => import('@/components/settings/GmailTestSettings'));
const InventorySettings = lazy(() => import('@/components/settings/InventorySettings'));
const PurchaseInvoicesSettings = lazy(
  () => import('@/components/settings/PurchaseInvoicesSettings')
);
const FinalizedPurchaseInvoicesList = lazy(
  () => import('@/components/settings/FinalizedPurchaseInvoicesList')
);
const DuplicateCleanupPage = lazy(() => import('@/components/settings/DuplicateCleanupPage'));
const Inventory2023DuplicateCleanupPage = lazy(
  () => import('@/components/settings/inventory-2023/Inventory2023DuplicateCleanupPage')
);
const BC2023Page = lazy(() => import('@/components/settings/BC2023Page'));
const VC2023Page = lazy(() => import('@/components/settings/VC2023Page'));
const Inventory2023Settings = lazy(() => import('@/components/settings/Inventory2023Settings'));
const CaisseSettings = lazy(() => import('@/components/settings/CaisseSettings'));

// Dev monitoring (lazy, chargé uniquement en dev)
const RouteChangeTracker = lazy(() =>
  import('@/components/dev/RouteChangeTracker').then(m => ({ default: m.RouteChangeTracker }))
);

// Fallback minimal pour Suspense (évite layout shift)
const PageFallback = () => null;

// Configuration React Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <DevRoleDashboardProvider>
            <PermissionsProvider>
              <StoreProvider>
                <ButtonColorsProvider>
                  <ThemeProvider>
                    <DashboardPreferencesProvider>
                      <ReminderProvider>
                        <Router basename={import.meta.env.BASE_URL}>
                          <ReminderSystem />
                          <PaymentDeletionWatcher />
                          {import.meta.env.DEV && (
                            <Suspense fallback={null}>
                              <RouteChangeTracker />
                            </Suspense>
                          )}
                          <Routes>
                            <Route path={ROUTES.LOGIN} element={<LoginPage />} />

                            <Route
                              path={ROUTES.SETTINGS.ROOT}
                              element={
                                <SettingsFeatureFlagsProvider>
                                  <AppErrorBoundary>
                                    <ProtectedRoute>
                                      <RoutePermissionGuard>
                                        <Suspense fallback={<PageFallback />}>
                                          <SettingsPage />
                                        </Suspense>
                                      </RoutePermissionGuard>
                                    </ProtectedRoute>
                                  </AppErrorBoundary>
                                </SettingsFeatureFlagsProvider>
                              }
                            >
                              <Route index element={<Navigate to="overview" replace />} />
                              <Route
                                path="overview"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <SettingsOverview />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/payment-methods"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PaymentMethodsSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/delivery-timings"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DeliveryTimingsSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/service-types"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <ServiceTypesSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/lift-types"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <LiftTypesSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/vendors"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <VendorsSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/stores"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <StoresManagement />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="documents/terms"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <TermsAndConditionsSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="access/users"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <SettingsAccessUsersRoute />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="access/permissions"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <SettingsAccessPermissionsRoute />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="access/devices"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <SettingsAccessDevicesRoute />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="personalization/home-links"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <HomepageLinksSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="personalization/dashboard-buttons"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DashboardButtonsByRoleSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="personalization/button-colors"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <ButtonColorSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="gmail-test"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <GmailTestSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="billit/purchase-invoices"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PurchaseInvoicesSettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="purchase-invoices-list"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <FinalizedPurchaseInvoicesList />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="billit/duplicate-cleanup"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DuplicateCleanupPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="billit/bc2023"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <BC2023Page />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="billit/vc2023"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <VC2023Page />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="billit/inventory-2023"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <Inventory2023Settings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="billit/inventory-2023-duplicate-cleanup"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <Inventory2023DuplicateCleanupPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="inventory"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <InventorySettings />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="caisse"
                                element={
                                  <Suspense
                                    fallback={
                                      <div className="flex h-full items-center justify-center">
                                        <LoadingSpinner />
                                      </div>
                                    }
                                  >
                                    <CaisseSettings />
                                  </Suspense>
                                }
                              />
                            </Route>

                            <Route
                              path={ROUTES.DEVICE_AUTHORIZATION}
                              element={
                                <ProtectedRoute>
                                  <Suspense fallback={<PageFallback />}>
                                    <DeviceAuthorizationPage />
                                  </Suspense>
                                </ProtectedRoute>
                              }
                            />

                            <Route
                              path={ROUTES.HOME}
                              element={
                                <AppErrorBoundary>
                                  <ProtectedRoute>
                                    <RoutePermissionGuard>
                                      <MainLayout />
                                    </RoutePermissionGuard>
                                  </ProtectedRoute>
                                </AppErrorBoundary>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <HomeLuxori />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.DASHBOARD}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <Home />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="recherche"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <SearchPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="ca-du-jour"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DailySalesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="statistiques-periode"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PeriodStatisticsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="statistiques-vendeur"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <VendorStatisticsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="surveillance-acomptes"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PaymentDeletionAuditPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.MARKETING_STUDIO}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <MarketingStudioListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.MARKETING_STUDIO_NEW}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <MarketingStudioPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.MARKETING_STUDIO.EDIT_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <MarketingStudioPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="devis"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DevisPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="devis/nouveau"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DevisFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="devis/:id/modifier"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DevisFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="devis/:id/voir"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DevisFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.CLIENTS}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <ClientsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.INVENTAIRE}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <InventairePage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.INVENTAIRE_2023}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <Inventaire2023Page />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.FOURNISSEURS}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <FournisseursPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ARTICLES}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <ArticlesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.DELIVERIES.BASE}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DeliveriesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.DELIVERIES.PREPARE_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DeliveryPreparationPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="payment-methods"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PaymentMethodsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="vendors"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <VendorsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="delivery-timings"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <DeliveryTimingsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="service-types"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <ServiceTypesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="lift-types"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <LiftTypesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="business/services-apres-vente"
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <AfterSalesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.AFTER_SALES.EDIT_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <AfterSalesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.AFTER_SALES.VIEW_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <AfterSalesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ACHATS}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <AchatsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.SUPPLIER_ORDERS}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <SupplierOrdersPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.PENDING_SUPPLIER_ORDERS}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PendingSupplierOrdersPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.PURCHASE_PAYMENTS}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <PaiementsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.FACTURES_A_PAYER}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <FacturesAPayerPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ORDERS.BASE}
                                element={
                                  <Suspense fallback={null}>
                                    <BonsDeCommandePage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ORDERS.NEW}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <OrderFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.ORDERS.EDIT_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <OrderFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.ORDERS.VIEW_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <OrderFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ORDERS2.BASE}
                                element={
                                  <Suspense fallback={null}>
                                    <BonsDeCommandePage2 />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ORDERS2.NEW}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <OrderFormPage2 />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.ORDERS2.EDIT_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <OrderFormPage2 />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.ORDERS2.VIEW_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <OrderFormPage2 />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTES.INVOICES.LIST}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <FacturesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTES.INVOICES.NEW}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <InvoiceFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.INVOICES.EDIT_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <InvoiceFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTES.ACCOMPTE_INVOICES.LIST}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <FacturesAcomptePage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.CREDIT_NOTES.BASE}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <CreditNotesPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.CREDIT_NOTES.NEW}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <CreditNotesFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.CREDIT_NOTES.EDIT_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <CreditNotesFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={ROUTE_PATTERNS.CREDIT_NOTES.VIEW_WITH_ID}
                                element={
                                  <Suspense fallback={<PageFallback />}>
                                    <CreditNotesFormPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.ORDERS.NEW_LEGACY}
                                element={<Navigate to={ROUTES.ORDERS.NEW} replace />}
                              />
                              <Route
                                path={ROUTE_PATTERNS.LEGACY.EDIT_WITH_ID}
                                element={<Navigate to={ROUTES.ORDERS.EDIT} replace />}
                              />
                              <Route
                                path={ROUTE_PATTERNS.LEGACY.VIEW_WITH_ID}
                                element={<Navigate to={ROUTES.ORDERS.VIEW} replace />}
                              />
                              <Route
                                path={RELATIVE_ROUTES.ADMIN}
                                element={
                                  <ProtectedRoute requiredRole="admin">
                                    <div className="p-6">
                                      <h1 className="text-2xl font-bold">Administration</h1>
                                      <p>Cette page est réservée aux administrateurs.</p>
                                    </div>
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={RELATIVE_ROUTES.MANAGEMENT}
                                element={
                                  <ProtectedRoute allowedRoles={['admin', 'gerant']}>
                                    <div className="p-6">
                                      <h1 className="text-2xl font-bold">Gestion</h1>
                                      <p>
                                        Cette page est accessible aux administrateurs et gérants.
                                      </p>
                                    </div>
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="*"
                                element={
                                  <div className="p-6 text-center">
                                    <h1 className="text-2xl font-bold text-gray-900">
                                      Page non trouvée
                                    </h1>
                                    <p className="mt-2 text-gray-600">
                                      La page que vous cherchez n'existe pas.
                                    </p>
                                  </div>
                                }
                              />
                            </Route>
                          </Routes>
                        </Router>
                      </ReminderProvider>
                    </DashboardPreferencesProvider>
                  </ThemeProvider>
                </ButtonColorsProvider>
              </StoreProvider>
            </PermissionsProvider>
          </DevRoleDashboardProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
